import { useState, useRef, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { Unit } from './types';

type Props = {
  value: number;
  unit: Unit;
  editable: boolean;
  onChange: (newValue: number) => void;
  align?: 'left' | 'right';
};

export function EditableTargetCell({ value, unit, editable, onChange, align = 'right' }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) setDraft(value.toString());
  }, [value, editing]);

  const formatted = (() => {
    if (unit === 'percentage') return `${value.toFixed(1)}%`;
    if (unit === 'multiplier') return `${value.toFixed(2)}x`;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  })();

  const commit = () => {
    const parsed = parseFloat(draft.replace(/[^\d.-]/g, ''));
    if (!Number.isNaN(parsed)) onChange(parsed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value.toString());
    setEditing(false);
  };

  if (!editable) {
    return <span className="text-gray-700">{formatted}</span>;
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        className={`h-9 w-28 rounded-lg border border-blue-400 px-2 ${align === 'right' ? 'text-right' : 'text-left'} text-sm outline-none focus:ring-2 focus:ring-blue-200`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="-mx-2 inline-flex h-8 cursor-text items-center gap-1.5 rounded-lg px-2 transition hover:bg-white/60"
    >
      <span className="text-gray-700">{formatted}</span>
      <Pencil className="w-3 h-3 text-gray-400" />
    </button>
  );
}
